import {Component, computed, effect, inject, input, output, signal} from '@angular/core';
import {TypeOption} from '@app/models';
import {FormBuilder, FormControl, ReactiveFormsModule, Validators} from '@angular/forms';
import {RecaptchaService} from '@app/services/recaptcha.service';
import {catchError, EMPTY, finalize, map, startWith, switchMap, tap} from 'rxjs';
import {GSheetService} from '@app/services/gsheet.service';
import {Message} from 'primeng/message';
import {InputText} from 'primeng/inputtext';
import {Button} from 'primeng/button';
import {FloatLabel} from 'primeng/floatlabel';
import {toSignal} from '@angular/core/rxjs-interop';
import {Checkbox} from 'primeng/checkbox';

@Component({
  selector: 'app-form',
  imports: [ReactiveFormsModule, Message, InputText, Button, FloatLabel, Checkbox],
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss'],
})
export class FormComponent {
  private fb = inject(FormBuilder).nonNullable;
  private recaptchaService = inject(RecaptchaService);
  private gSheetService = inject(GSheetService);

  private readonly REGEX_MAIL = `^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$`;

  public result = input.required<TypeOption>();
  public afterSubmit = output<void>();

  protected loading = signal<boolean>(false);

  protected isValid = computed(() => {
    return this.formStatus() && this.consent();
  })

  protected formGroup = this.fb.group({
    firstName: ['', [Validators.required]],
    lastName: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.pattern(this.REGEX_MAIL)]],
    result: [TypeOption.A, [Validators.required]],
    consent: [false, [Validators.required]]
  });

  private formStatus = toSignal(
    this.formGroup.statusChanges.pipe(
      startWith(this.formGroup.status),
      map(status => status === 'VALID')
    ),
    {initialValue: false}
  );

  private consent = toSignal(
    this.formGroup.valueChanges.pipe(
      startWith(this.formGroup.value),
      map(value => value.consent)
    ),
    {initialValue: false}
  );

  constructor() {
    effect(() => {
      const result = this.result();
      if (result) {
        this.formGroup.controls.result.setValue(result);
      }
    });
  }

  protected isInvalid(control: FormControl) {
    return control.invalid && control.dirty;
  }

  protected validate() {
    if (!this.formGroup.valid) {
      return;
    }

    this.loading.set(true);

    this.recaptchaService.getToken('submit_quiz').pipe(
      switchMap(token => {
        const payload = {...this.formGroup.getRawValue()};
        return this.gSheetService.saveResult(payload, token);
      }),
      tap(() => this.afterSubmit.emit()),
      catchError((err, caught) => {
        console.error(err);
        return EMPTY;
      }),
      finalize(() => this.loading.set(false))
    ).subscribe();

  }
}
